import React, { useContext, useEffect, useState } from 'react';
import "./AdministrativeExpensesView.css";
import axios from "axios";
import AuthContext from "../../../contexts/AuthContext";
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import CellInfoModal from "../../../components/CellInfoModal/CellInfoModal";
import { currencyFormat } from "../../../utils/currencyFormat";
import { Badge, FileInput, Flex, Group, Loader, NumberInput, Paper, Select, Skeleton, Stack, Switch, Table, Tabs, Text } from "@mantine/core";
import { DateInput, DatePickerInput } from "@mantine/dates";
import AppContext from "../../../contexts/AppContext";
import moment from "moment";
import { IconBook, IconCalendar, IconCheck, IconCoin, IconCurrencyDollar, IconExclamationCircle, IconPhoto, IconSchool, IconX } from '@tabler/icons-react';
import showNotification from '../../../utils/showNotification';
import { ERROR_HANDLER } from '../../../utils/error-handler';
import { renderFile } from '../../../utils/renderFile';
import { isBase64 } from '../../../utils/isBase64';
import useS3 from '../../../hooks/useS3';


const findById = (_staffUsers, id) => {
    const sellerObj = _staffUsers.find((_user) => _user.value === id);
    return sellerObj ? sellerObj.label : id;
};

/**
 * Calculate payment details based on row data
 * Nueva estructura: cada installment es un documento separado con campos planos
 * @param row
 * @returns {{commission: string, installmentNumber: number|null, paymentType: string, currency: string}}
 */
function calculatePaymentDetails(row) {
    try {
        if (!row || !row.type) {
            throw new Error("Datos incompletos o inválidos en la fila");
        }

        const isInstallment = row.isInstallment;
        const paymentType = isInstallment ? "Cuota" : "Pago único";
        const installmentNumber = isInstallment ? row.installment_number : null;
        const commissionAmount = row.installment_commission ?? 0;
        const currency = row.installment_currency ?? "ARS";

        return {
            paymentType,
            commission: currencyFormat(commissionAmount),
            installmentNumber,
            currency
        };
    } catch (error) {
        console.error("Error al calcular detalles de pago:", error.message);
        return { paymentType: "Desconocido", commission: currencyFormat(0), installmentNumber: null, currency: "ARS" };
    }
}
const SimpleSalesDetailsTable = ({ data }) => {
    return (
        <Table.ScrollContainer>
            <Table verticalSpacing="xs">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Fecha</Table.Th>
                        <Table.Th>Tipo</Table.Th>
                        <Table.Th>Capacitación</Table.Th>
                        <Table.Th>Estudiante</Table.Th>
                        <Table.Th>Comisión</Table.Th>
                        <Table.Th>Estado</Table.Th> {/* Añadimos cabecera */}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.map((row, index) => {
                        const { paymentType, commission, installmentNumber, currency } = calculatePaymentDetails(row);
                        const typeLabel = installmentNumber ? `${paymentType} ${installmentNumber}` : paymentType;

                        const is_disabled = !row.is_admin_check;
                        const rowOpacity = is_disabled ? 0.35 : 1;

                        return (
                            <Table.Tr
                                key={row._id || index}
                                style={{
                                    opacity: rowOpacity,
                                    backgroundColor: is_disabled ? 'var(--mantine-color-gray-0)' : 'transparent',
                                    transition: 'opacity 0.2s ease'
                                }}
                            >
                                <Table.Td>{moment(new Date(row.installment_payment_date || row.created_at)).format("DD/MM/YYYY")}</Table.Td>
                                <Table.Td>{typeLabel}</Table.Td>
                                <Table.Td>{row.details?.[0]?.description ?? "-"}</Table.Td>
                                <Table.Td>{`${row.payer?.name ?? ""} ${row.payer?.lastname ?? ""}`}</Table.Td>
                                <Table.Td fw={is_disabled ? 400 : 700}>
                                    {`$${commission} ${currency}`}
                                </Table.Td>
                                <Table.Td>
                                    {is_disabled ? (
                                        <Badge color='red'><IconX size={10} /></Badge>
                                    ) : (
                                        <Badge color='rgb(158, 192, 152)'><IconCheck size={10} /></Badge>
                                    )}
                                </Table.Td>
                            </Table.Tr>
                        );
                    })}
                </Table.Tbody>
            </Table>
        </Table.ScrollContainer>
    );
};

function AdministrativeExpensesView() {
    const { authToken } = useContext(AuthContext);
    const { staffUsers, dollarValue } = useContext(AppContext)
    const [expensesData, setExpensesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cellModal, setCellModal] = useState(null)
    const [reFetch, setReFetch] = useState(false)



    // Get initDate as this month's first day
    const firstDayOfThisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const currentDay = new Date();
    const [dateFilter, setDateFilter] = useState([firstDayOfThisMonth, currentDay]);

    // COLUMN DEFINITIONS
    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor("name", {
            cell: (info) => info.getValue(),
            header: "Nombre",
            enableColumnFilter: true,
            enableSorting: false,
            filter: "text",
            filterFn: "includesString",
        }),

        columnHelper.accessor("totals.totalUSD", {
            cell: (info) => {
                return <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue()}
                </span>
            },
            header: "Total USD",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("totals.totalARS", {
            cell: (info) => {
                const value = info.getValue() || 0
                const amount_in_ars = value * dollarValue

                return <span className="AdministrativeExpensesView__cell--center">
                    {amount_in_ars}
                </span>
            },
            header: "Total ARS",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) =>
                <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue().length}
                </span>,
            header: "Clases",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) => {
                const lessonData = info.getValue() || []
                const hasPendingPay = lessonData.some(lesson => !lesson.fee.is_paid);
                const teacherName = info.row.original.name



                return <span
                    className="AdministrativeExpensesView__cell"
                    onClick={() => setCellModal({

                        text: <Stack gap="xl" py="md">
                            <Paper withBorder p="md" maw='max-content' radius="md" bg="var(--mantine-color-gray-0)">
                                <Text mb={4} size='xs'>Glosario</Text>
                                <Group>
                                    <IconSchool size={14} />
                                    <Text size='xs'>Nombre de unidad academica</Text>
                                </Group>
                                <Group>
                                    <IconBook size={14} />
                                    <Text size='xs'>Nombre de la clase</Text>
                                </Group>
                            </Paper>
                            {lessonData.map((_l, index) => {
                                const isOnlyOneAcademicUnit = _l?.academicUnits?.length === 1

                                return <Paper key={_l.lesson_id} withBorder p="md" radius="md" bg="var(--mantine-color-gray-0)">

                                    <Stack gap='sm'>
                                        <Stack justify="space-between">
                                            {!isOnlyOneAcademicUnit
                                                ? <Stack>
                                                    {_l.academicUnits.map(item => <Group gap="xs" key={item}>
                                                        <IconSchool size={16} />
                                                        <Text fw={500} size="sm">{item}</Text>
                                                    </Group>)}
                                                </Stack>
                                                : <Group gap="xs">
                                                    <IconSchool size={16} />
                                                    <Text fw={500} size="sm">{_l.academicUnits[0]}</Text>
                                                </Group>}
                                            <Group gap="xs">
                                                <IconBook size={16} />
                                                <Text fw={500} size="sm">{_l.name}</Text>
                                            </Group>
                                            <Badge color="gray" variant="outline">Clase #{index + 1}</Badge>
                                        </Stack>
                                        <Flex
                                            gap="md"
                                            key={_l.lesson_id}
                                            direction={{ base: 'column', sm: 'row' }}
                                            align={{ sm: 'flex-end' }}
                                        >

                                            <DateInput
                                                label="Fecha de clase"
                                                leftSection={<IconCalendar size={16} />}
                                                value={_l.date ? new Date(_l.date) : null}
                                                style={{ flex: 1 }}
                                                readOnly
                                            />

                                            <NumberInput
                                                label="Monto de tarifa"
                                                placeholder="0.00"
                                                hideControls
                                                prefix="$ "
                                                min={0}
                                                leftSection={<IconCurrencyDollar size={16} />}
                                                style={{ flex: 1 }}
                                                value={_l.fee.currency?.toUpperCase() === 'ARS' ? _l.fee.amount * dollarValue : _l.fee.amount}
                                                readOnly

                                            />

                                            <Select
                                                label="Moneda"
                                                data={['USD', 'ARS']}
                                                leftSection={<IconCoin size={16} />}
                                                value={_l.fee.currency}
                                                style={{ flex: 0.5 }}
                                                readOnly
                                            />


                                            <SwitchPaidButton
                                                is_paid={_l.fee?.is_paid || false}
                                                lesson_id={_l.lesson_id}
                                                user_id={_l?.teacher_id}
                                                afterSubmit={() => setReFetch(prev => !prev)}
                                            />

                                        </Flex>

                                        <UploadReceiptInput
                                            initialValue={_l.fee?.receipt_url || ""}
                                            lesson_id={_l.lesson_id}
                                            user_id={_l.teacher_id}
                                            teacher_name={teacherName}
                                            lesson_name={_l.name}
                                            afterSubmit={() => setReFetch(prev => !prev)}
                                        />
                                    </Stack>
                                </Paper>

                            }
                            )}</Stack>,
                        title: 'Clases dictadas',
                    })}
                >
                    {hasPendingPay && <IconExclamationCircle size={18} style={{ color: 'var(--mantine-color-red-6)', marginRight: ".2rem" }} />}
                    Ver detalles
                </span>
            },
            header: "Detalle",
            enableColumnFilter: false,
            enableSorting: true,
        }),
    ];
    const modColumns = [
        columnHelper.accessor("name", {
            cell: (info) => info.getValue(),
            header: "Nombre",
            enableColumnFilter: true,
            enableSorting: false,
            filter: "text",
            filterFn: "includesString",
        }),
        columnHelper.accessor("", {
            cell: (info) => {
                const { staffInfo, lessonsData } = info.row.original;
                if (!staffInfo.lessonFee) return null;
                return (
                    <span className="AdministrativeExpensesView__cell--center">
                        {currencyFormat(staffInfo.lessonFee * lessonsData.length)}
                    </span>);
            },
            header: "Total",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("staffInfo.feeCurrency", {
            cell: (info) =>
                <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue()}
                </span>,
            header: "Moneda",
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: 'ARS', label: 'ARS' },
                { value: 'USD', label: 'USD' },
            ],
            size: 88,
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;
                return row.original.staffInfo.feeCurrency.toLowerCase() === filterValue.toLowerCase();
            }
        }),
        columnHelper.accessor("staffInfo", {
            cell: (info) => {
                const value = info.getValue().lessonFee
                const currency = info.getValue().feeCurrency
                if (!value) return null;

                const amount = currency?.toUpperCase() === "USD" ? value : value * dollarValue

                return <span className="AdministrativeExpensesView__cell--center">
                    {amount} {currency}
                </span>
            },
            header: "Valor clase",
            enableColumnFilter: false,
            enableSorting: true,
            size: 104,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) =>
                <span className="AdministrativeExpensesView__cell--center">
                    {info.getValue().length}
                </span>,
            header: "Clases",
            enableColumnFilter: false,
            enableSorting: true,
            size: 88,
        }),
        columnHelper.accessor("lessonsData", {
            cell: (info) => <span
                className="AdministrativeExpensesView__cell"
                onClick={() => setCellModal({
                    text: <ul>{info.getValue().map(_l => <li className="AdministrativeExpensesView__ul">{_l.name}</li>)}</ul>,
                    title: 'Clases dictadas'
                })}
            >{info.getValue().map(_l => _l.name).join(",")}</span>,
            header: "Detalle",
            enableColumnFilter: false,
            enableSorting: true,
        }),
    ];
    const salesColumns = [
        columnHelper.accessor("sellerId", {
            cell: (info) => {
                const sellerId = info.getValue();
                return findById(staffUsers, sellerId);
            },
            header: "Vendedor",
            enableColumnFilter: false,
            enableSorting: true
        }),
        columnHelper.accessor("commissions.totalARS", {
            cell: (info) => {
                return `$ ${currencyFormat(info.getValue())}`;
            },
            header: "Comisiones ARS",
            enableColumnFilter: false,
            enableSorting: true,
            size: 120,
        }),
        columnHelper.accessor("commissions.totalUSD", {
            cell: (info) => {
                return `$ ${currencyFormat(info.getValue())}`;
            },
            header: "Comisiones USD",
            enableColumnFilter: false,
            enableSorting: true,
            size: 120,
        }),
        columnHelper.accessor("sales", {
            cell: (info) => {
                const salesData = info.row.original.sales || [];

                const hasPendingPayments = salesData.some(payment => !payment.is_admin_check);

                return (
                    <Group gap="xs" justify="center" style={{ cursor: 'pointer' }} onClick={() => setCellModal({
                        text: <SimpleSalesDetailsTable data={salesData} />,
                        title: `Detalle de ventas de ${findById(staffUsers, info.row.original.sellerId)}`,
                        darkMode: false
                    })}>
                        {/* UI: Renderizar el icono de exclamación con Tooltip si hay pendientes */}
                        {hasPendingPayments && (
                            <IconExclamationCircle size={18} style={{ color: 'var(--mantine-color-red-6)' }} />
                        )}

                        <span className="AdministrativeExpensesView__cell--center">
                            Ver detalles
                        </span>
                    </Group>
                );
            },
            header: "Detalle",
            enableColumnFilter: false,
            enableSorting: true,
            size: 110
        })
    ];

    // DATA INITIALIZATION
    const init = async () => {
        setLoading(true);
        const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/getExpenses?initDate=${dateFilter[0].toISOString()}&endDate=${dateFilter[1].toISOString()}`;
        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
        };
        const expensesResponse = await axios.get(url, config);

        // Sort data by lessonsData length
        expensesResponse.data.teachers.sort((a, b) => b.lessonsData.length - a.lessonsData.length);
        expensesResponse.data.moderators.sort((a, b) => b.lessonsData.length - a.lessonsData.length);

        setExpensesData(expensesResponse.data);
        setLoading(false);
    };

    useEffect(() => {
        if (dateFilter[0] && dateFilter[1])
            init();
    }, [dateFilter, reFetch]);


    return (
        <div className="AdministrativeExpensesView__container">
            <div className="AdministrativeExpensesView__filters">
                <DatePickerInput
                    classNames={{ root: "moderationContainer-DatePicker-root" }}
                    type="range"
                    label="Mostrando datos de:"
                    value={dateFilter}
                    onChange={(value) => setDateFilter(value)}
                />
                <span>
                    * Por defecto se muestran datos del mes actual (desde el 1ro hasta la fecha actual)
                </span>
            </div>

            {loading && < Skeleton h='400px' w='100%' />}
            {(!loading) && <Tabs
                defaultValue="Profesores"
                style={{ width: "100%" }}
            >
                <Tabs.List>
                    <Tabs.Tab value="Profesores">
                        Profesores
                    </Tabs.Tab>
                    <Tabs.Tab value="Moderadores">
                        Moderadores
                    </Tabs.Tab>
                    <Tabs.Tab value="Vendedores">
                        Vendedores
                    </Tabs.Tab>
                </Tabs.List>

                {expensesData?.teachers &&
                    <Tabs.Panel value="Profesores" pt={20}>
                        <TanStackTable
                            loading={loading}
                            data={expensesData.teachers}
                            columns={columns}
                        />
                    </Tabs.Panel>}

                {expensesData?.moderators &&
                    <Tabs.Panel value="Moderadores" pt={20}>
                        <TanStackTable
                            loading={loading}
                            data={expensesData.moderators}
                            columns={modColumns}
                        />
                    </Tabs.Panel>
                }
                {expensesData?.sales &&
                    <Tabs.Panel value="Vendedores" pt={20}>
                        <TanStackTable
                            loading={loading}
                            data={expensesData.sales}
                            columns={salesColumns}
                        />
                    </Tabs.Panel>
                }
            </Tabs>}


            <CellInfoModal opened={cellModal} onCloseFunct={setCellModal} text={cellModal?.text}
                title={cellModal?.title} darkMode={cellModal?.darkMode} />
        </div>
    );
}

export default AdministrativeExpensesView;


const SwitchPaidButton = ({ is_paid, lesson_id, user_id, afterSubmit }) => {
    const { authToken } = useContext(AuthContext)
    const [isLoading, setIsLoading] = useState(false)

    const [currentState, setCurrentState] = useState(is_paid)

    const handleChangeSubmit = async (new_state) => {
        try {
            setIsLoading(true)

            const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/lessons/payments`;
            const config = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
            }

            const jsonData = {
                lesson_id,
                user_id,
                field: "is_paid",
                value: new_state
            }

            await axios.put(url, jsonData, config);

            setCurrentState(new_state)

            const messageText = new_state ? "pagada" : "sin pagar"
            showNotification({
                color: new_state ? "green" : "yellow",
                status: new_state ? "success" : "warning",
                title: "Cambio de estado.",
                message: `La clase ha sido marcada como ${messageText}.`,
            });
            if (afterSubmit) {
                afterSubmit()
            }
        } catch (error) {
            console.log(error)
            showNotification({
                color: "red",
                status: "error",
                title: "Error al cambiar el pago de la clase",
                message: `Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError}`,
                autoClose: 12000
            });
        } finally {
            setIsLoading(false)
        }
    }



    return <Flex direction='column'>
        <span style={{ fontSize: "var(--fs-small)" }}>Pagada</span>
        {isLoading ? <Loader />
            : <Switch
                disabled={isLoading}
                checked={currentState}
                onChange={(event) => handleChangeSubmit(event.currentTarget.checked)}
                color="teal"
                size="lg"
                thumbIcon={
                    currentState ? (
                        <IconCheck size={12} color="var(--mantine-color-teal-6)" stroke={3} />
                    ) : (
                        <IconX size={12} color="var(--mantine-color-red-6)" stroke={3} />
                    )
                }
            />
        }
    </Flex>
}


const UploadReceiptInput = ({ lesson_id, user_id, teacher_name, lesson_name, afterSubmit, initialValue }) => {
    const { authToken } = useContext(AuthContext)
    const [receiptImage, setCurrentReceiptImage] = useState(initialValue)
    const { uploadToS3 } = useS3();
    const [isReceiptLoading, setIsReceiptLoading] = useState(false)

    const handleSubmitReceipt = async (image) => {
        try {
            setIsReceiptLoading(true)
            if (image && isBase64(image)) {
                const clearTitle = `Comprobante-profesor-${teacher_name}-${lesson_name}`.replace(/[^\w\s]/g, "").trim();
                const receipt_url = await uploadToS3({
                    image: image,
                    fileName: `${clearTitle}-${moment().format("DD-MM-YYYY")}`,
                    fileDirectory: "receipts",
                    isRequired: true,
                });

                if (!receipt_url) {
                    return showNotification({
                        color: "red",
                        status: "error",
                        title: "No se ha encontrado la URL del comprobante",
                        message: "No se pudo obtener la URL del comprobante.",
                        autoClose: 12000
                    });
                }

                setCurrentReceiptImage(receipt_url)

                const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/administracion/lessons/payments`;
                const config = {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                    },
                }

                await axios.put(url, {
                    lesson_id,
                    user_id,
                    field: "receipt_url",
                    value: receipt_url
                }, config);

                showNotification({
                    color: "green",
                    status: "success",
                    title: "Operación exitosa!",
                    message: "El comprobante ha sido cargado correctamente.",
                });

                if (afterSubmit) afterSubmit()
            }
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Error al cargar el comprobante de pago",
                message: `Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError}`,
                autoClose: 12000
            });
        } finally {
            setIsReceiptLoading(false)
        }
    }


    return (
        <Flex gap="md" align="flex-end">
            <FileInput
                label="Cargar comprobante de pago"
                description="Si no corresponde, no cargar ningún comprobante."
                w={"100%"}
                loading={isReceiptLoading}
                disabled={isReceiptLoading}
                placeholder="Cargar comprobante"
                leftSection={<IconPhoto size={30} />}
                onChange={(file) => {
                    renderFile(file).then((_fileBase64) => {
                        handleSubmitReceipt(_fileBase64)  // llama directamente con el base64
                    });
                }}
                accept={["image/*", "application/pdf"]}
            />
            {receiptImage && (
                <Flex>
                    <img
                        src={receiptImage}
                        className='SalesInstallmentsConfirmModal-receipt-image-preview'
                    />
                </Flex>
            )}
        </Flex>
    )
}